import PeopleIcon from '@mui/icons-material/People';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { WELCOME_SPEECH_DATA } from '../../data/welcome-speech-data';

const WelcomeSpeech = () => {
    const theme = useTheme();

    const eventHighlights = [
        {
            icon: (
                <PeopleIcon
                    sx={{ fontSize: 24, color: theme.palette.primary.main }}
                />
            ),
            title: '500+ Participants',
            description: 'Medical professionals from across the region',
        },
        {
            icon: (
                <SchoolIcon
                    sx={{ fontSize: 24, color: theme.palette.success.main }}
                />
            ),
            title: 'Expert Speakers',
            description: 'Leading specialists in cardiovascular care',
        },
        {
            icon: (
                <TrendingUpIcon
                    sx={{ fontSize: 24, color: theme.palette.warning.main }}
                />
            ),
            title: 'Latest Research',
            description: 'Cutting-edge medical advancements',
        },
    ];

    return (
        <Box bgcolor={theme.palette.alternate.main}>
            <Container>
                <Box
                    sx={{
                        py: { xs: 8, md: 12 },
                        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, white 50%, ${theme.palette.primary.main}08 100%)`,
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Background decorative elements */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -50,
                            right: -50,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                            zIndex: 0,
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -100,
                            left: -100,
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${theme.palette.secondary.main}10, ${theme.palette.primary.main}10)`,
                            zIndex: 0,
                        }}
                    />

                    <Container
                        maxWidth="lg"
                        sx={{ position: 'relative', zIndex: 1 }}
                    >
                        <Card
                            sx={{
                                height: '100%',
                                borderRadius: 4,
                                boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
                                border: `1px solid ${theme.palette.grey[200]}`,
                                transition:
                                    'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                },
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 3,
                                    }}
                                >
                                    <RecordVoiceOverIcon
                                        sx={{
                                            fontSize: 40,
                                            color: theme.palette.primary.main,
                                            mr: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.primary',
                                            fontSize: {
                                                xs: '1.5rem',
                                                md: '2rem',
                                            },
                                        }}
                                    >
                                        Welcome Speech
                                    </Typography>
                                </Box>

                                <Divider sx={{ mb: 3 }} />

                                {WELCOME_SPEECH_DATA.map((item, index) => (
                                    <Box key={index} sx={{ mb: 4 }}>
                                        <Box
                                            sx={{
                                                float: {
                                                    xs: 'none',
                                                    md: 'left',
                                                },
                                                width: { xs: '100%', md: 260 },
                                                mr: { md: 3 },
                                                mb: 1,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={item.image}
                                                alt={item.name}
                                                sx={{
                                                    width: '100%',
                                                    borderRadius: 3,
                                                    boxShadow:
                                                        '0 12px 30px rgba(0,0,0,0.12)',
                                                    border: `1px solid ${theme.palette.grey[200]}`,
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        </Box>

                                        {item.speech.map((item, index) => (
                                            <Typography
                                                key={index}
                                                variant="body2"
                                                align="justify"
                                                sx={{
                                                    color: 'text.primary',
                                                    lineHeight: 1.6,
                                                    mb: 0.8,
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: item,
                                                }}
                                            />
                                        ))}

                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.primary' }}
                                        >
                                            Warm regards,
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.primary',
                                                fontWeight: 600,
                                                mb: 0.5,
                                            }}
                                        >
                                            {item.name}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'text.primary' }}
                                            dangerouslySetInnerHTML={{
                                                __html: `${item.title} <br /> ${item.notes}`,
                                            }}
                                        ></Typography>
                                        <Box sx={{ clear: 'both' }} />
                                    </Box>
                                ))}

                                {/* Event Highlights */}
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            mb: 3,
                                            color: 'text.primary',
                                        }}
                                    >
                                        Event Highlights
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {eventHighlights.map(
                                            (highlight, index) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={4}
                                                    key={index}
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            backgroundColor:
                                                                theme.palette
                                                                    .grey[50],
                                                            borderRadius: 2,
                                                            textAlign: 'center',
                                                            border: `1px solid ${theme.palette.grey[200]}`,
                                                            transition:
                                                                'all 0.3s ease',
                                                            '&:hover': {
                                                                backgroundColor:
                                                                    'white',
                                                                transform:
                                                                    'translateY(-2px)',
                                                                boxShadow:
                                                                    '0 8px 25px rgba(0,0,0,0.1)',
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ mb: 1 }}>
                                                            {highlight.icon}
                                                        </Box>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                fontWeight: 600,
                                                                mb: 0.5,
                                                                color: 'text.primary',
                                                            }}
                                                        >
                                                            {highlight.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontSize:
                                                                    '0.8rem',
                                                            }}
                                                        >
                                                            {
                                                                highlight.description
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ),
                                        )}
                                    </Grid>
                                </Box>
                            </CardContent>
                        </Card>
                    </Container>
                </Box>
            </Container>
        </Box>
    );
};

export default WelcomeSpeech;
